/* eslint-env browser */

import { createBuffer, ByteBuffer } from 'node-forge/lib/util'
import asn1 from 'node-forge/lib/asn1'
import { setPrivateKey, setPublicKey } from 'node-forge/lib/rsa'

import { str2ab, ab2str } from './utils'
import symmetric from './symmetric'
import { getRandomValues } from './subtle/getRandomValues'
import {
  decrypt as subtleSymmetricDecrypt,
  decryptOld as subtleSymmetricDecryptOld,
  loadSessionKey as subtleLoadSessionKey,
} from './subtle/symmetric'
import { DEFAULT_RSA_KEYSIZE } from './constants'
import { base64ToBigNum } from './bignum'

export interface KeyPair {
  publicKey: any
  privateKey?: any
}

const decryptRSA = function (key: KeyPair, ciphertext: string): Promise<Uint8Array> {
  try {
    const output: string = key.privateKey.decrypt(ciphertext, 'RSA-OAEP')
    return Promise.resolve(str2ab(output))
  } catch (err) {
    return Promise.reject(err)
  }
}

export default {
  generateKeyPair: async function (
    stepCallback?: () => void,
    keyLength?: number
  ): Promise<KeyPair> {
    const keypair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: keyLength || DEFAULT_RSA_KEYSIZE,
        publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt']
    )

    const privateKey: JsonWebKey = await crypto.subtle.exportKey('jwk', keypair.privateKey)

    const e = base64ToBigNum(privateKey.e!, { url: true })
    const n = base64ToBigNum(privateKey.n!, { url: true })
    const d = base64ToBigNum(privateKey.d!, { url: true })
    const p = base64ToBigNum(privateKey.p!, { url: true })
    const q = base64ToBigNum(privateKey.q!, { url: true })
    const dp = base64ToBigNum(privateKey.dp!, { url: true })
    const dq = base64ToBigNum(privateKey.dq!, { url: true })
    const qi = base64ToBigNum(privateKey.qi!, { url: true })

    return {
      privateKey: setPrivateKey(n, e, d, p, q, dp, dq, qi),
      publicKey: setPublicKey(n, e),
    }
  },

  encrypt: function (plaintext: ByteBuffer, key: KeyPair): Promise<ByteBuffer> {
    const sessionKey = getRandomValues(32)

    return Promise.all([
      symmetric.encrypt(plaintext, sessionKey),
      key.publicKey.encrypt(ab2str(sessionKey), 'RSA-OAEP'),
    ]).then(([encryptedData, encryptedSessionKey]) => {
      const skAsn1 = asn1.create(
        asn1.Class.UNIVERSAL,
        asn1.Type.BITSTRING,
        false,
        this.bitStringToDer(encryptedSessionKey).bytes()
      )
      const edAsn1 = asn1.create(
        asn1.Class.UNIVERSAL,
        asn1.Type.BITSTRING,
        false,
        this.bitStringToDer(encryptedData).bytes()
      )

      const seq = asn1.create(asn1.Class.UNIVERSAL, asn1.Type.SEQUENCE, true, [skAsn1, edAsn1])

      return asn1.toDer(seq)
    })
  },

  decryptToBuffer: function (
    ciphertext: ByteBuffer | string,
    key: KeyPair,
    useWebCrypto: boolean = false
  ): Promise<Uint8Array> {
    const seq = asn1.fromDer(ciphertext, {
      strict: true,
      decodeBitStrings: false,
    })

    const encryptedSessionKey = this.derToBitString(seq.value[0].value)
    const encryptedData = this.derToBitString(seq.value[1].value)

    return decryptRSA(key, encryptedSessionKey).then((sessionKey) => {
      if (useWebCrypto) {
        return subtleLoadSessionKey(sessionKey).then((sessionKey) =>
          subtleSymmetricDecrypt(str2ab(encryptedData), sessionKey)
        )
      } else {
        return symmetric.decryptToBuffer(createBuffer(encryptedData), sessionKey)
      }
    })
  },

  decryptOldToBuffer: function (
    ciphertext: ByteBuffer,
    key: KeyPair,
    useWebCrypto: boolean = false
  ): Promise<Uint8Array> {
    const seq = asn1.fromDer(ciphertext, {
      strict: true,
      decodeBitStrings: false,
    })

    const encryptedSessionKey = this.derToBitString(seq.value[0].value)
    const encryptedData = this.derToBitString(seq.value[1].value)
    let sessionKey: Uint8Array = str2ab(key.privateKey.decrypt(encryptedSessionKey, 'NONE'))

    if (sessionKey.byteLength === 256) {
      // the bignum was right-padded with zeros to 256 bytes
      sessionKey = sessionKey.slice(256 - 32)
    }

    if (useWebCrypto) {
      return subtleLoadSessionKey(sessionKey)
        .then((sessionKey) => subtleSymmetricDecryptOld(str2ab(encryptedData), sessionKey))
        .then((plainData) => {
          const ab = plainData.buffer
          const result = new Uint8Array(ab, plainData.byteOffset, plainData.byteLength - 32)

          return result
        })
    } else {
      return symmetric
        .decryptOldToBuffer(createBuffer(encryptedData), sessionKey)
        .then((plainData) => {
          const ab = plainData.buffer

          return new Uint8Array(ab, plainData.byteOffset, plainData.byteLength - 32)
        })
    }
  },

  bitStringToDer: function (bs: ByteBuffer | string): ByteBuffer {
    const bytes: ByteBuffer = createBuffer()

    bytes.putByte(0x00)

    // is it a byte buffer?
    if (typeof (bs as ByteBuffer).bytes === 'function') {
      bs = (bs as ByteBuffer).bytes()
    }

    bytes.putBytes(bs as string)

    return bytes
  },

  derToBitString: function (bytes: string): string {
    // just dump the first byte
    return bytes.substr(1)
  },
}
